
import { useEffect, useState, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useBugReports } from "@/hooks/useBugReports";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Bug, Star, Zap, FileText, Trophy, User } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { SUPABASE_URL } from "@/integrations/supabase/client";

interface BugReportUserCount {
  userId: string;
  email: string;
  name?: string;
  count: number;
}

interface BugReportsStatsCardProps {
  rearrangingEnabled?: boolean;
  onRemove?: (id: string) => void;
}

export function BugReportsStatsCard({ rearrangingEnabled = false, onRemove }: BugReportsStatsCardProps) {
  const { useBugReportsQuery } = useBugReports();
  const { data: reports = [], isLoading } = useBugReportsQuery();
  const { getAccessToken } = useAuth();
  
  const [userCounts, setUserCounts] = useState<BugReportUserCount[]>([]);
  const fetchingUsers = useRef(false);
  
  // Calculate stats
  const totalReports = reports.length;
  const bugReports = reports.filter(report => report.category === 'Bug').length;
  const featureRequests = reports.filter(report => report.category === 'Feature').length;
  const hotfixes = reports.filter(report => report.category === 'Hotfix').length;
  
  useEffect(() => {
    // Only proceed if we have reports and are not currently fetching
    if (reports.length === 0 || fetchingUsers.current) {
      return;
    }
    
    // Calculate user counts
    const userMap = new Map<string, { count: number; email: string; name?: string }>();
    
    reports.forEach(report => {
      const userId = report.userId;
      if (userMap.has(userId)) {
        userMap.get(userId)!.count += 1;
      } else {
        // For now we just store the userId, we'll try to get names in a separate step
        userMap.set(userId, { count: 1, email: userId });
      }
    });
    
    // Sort users by count
    const sortedUsers = Array.from(userMap.entries())
      .map(([userId, data]) => ({
        userId,
        email: data.email,
        name: data.name,
        count: data.count
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5); // Get top 5
    
    const fetchUserNames = async () => {
      try {
        // Set the flag to indicate we're fetching users
        fetchingUsers.current = true;
        console.log("Bug Reports component: Fetching user names");
        
        const accessToken = await getAccessToken();
        
        if (!accessToken || sortedUsers.length === 0) {
          setUserCounts(sortedUsers);
          fetchingUsers.current = false;
          return;
        }
        
        // Use the same edge function that AdminDashboard uses
        const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-get-users`, {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        });
        
        if (!response.ok) {
          console.error(`Error fetching users: ${response.statusText}`);
          setUserCounts(sortedUsers);
          fetchingUsers.current = false;
          return;
        }
        
        const users = await response.json();
        
        // Create a map of user IDs to display names
        const userDisplayNames = new Map();
        users.forEach((user) => {
          const displayName = user.name || 
                             (user.email ? user.email.split('@')[0] : null) ||
                             'Unknown User';
          userDisplayNames.set(user.id, {
            name: displayName,
            email: user.email
          });
        });
        
        // Update user counts with display names
        const updatedUsers = sortedUsers.map(userCount => {
          const userInfo = userDisplayNames.get(userCount.userId);
          if (userInfo) {
            return {
              ...userCount,
              name: userInfo.name,
              email: userInfo.email || userCount.email
            };
          }
          return userCount;
        });
        
        setUserCounts(updatedUsers);
      } catch (error) {
        console.error("Error fetching user display names:", error);
        setUserCounts(sortedUsers);
      } finally {
        fetchingUsers.current = false;
      }
    };
    
    fetchUserNames();
  }, [reports, getAccessToken]);
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <FileText className="h-5 w-5 mr-2 text-primary" />
          Bug Reports Statistics
        </CardTitle>
        <CardDescription>Overview of bug reports and feature requests</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="flex flex-col items-center justify-center p-3 bg-muted rounded-lg">
            <FileText className="h-5 w-5 mb-1 text-primary" />
            <span className="text-2xl font-bold">{totalReports}</span>
            <span className="text-xs text-muted-foreground">Total Reports</span>
          </div>
          <div className="flex flex-col items-center justify-center p-3 bg-muted rounded-lg">
            <Bug className="h-5 w-5 mb-1 text-destructive" />
            <span className="text-2xl font-bold">{bugReports}</span>
            <span className="text-xs text-muted-foreground">Bugs</span>
          </div>
          <div className="flex flex-col items-center justify-center p-3 bg-muted rounded-lg">
            <Star className="h-5 w-5 mb-1 text-warning" />
            <span className="text-2xl font-bold">{featureRequests}</span>
            <span className="text-xs text-muted-foreground">Features</span>
          </div>
          <div className="flex flex-col items-center justify-center p-3 bg-muted rounded-lg">
            <Zap className="h-5 w-5 mb-1 text-yellow-500" />
            <span className="text-2xl font-bold">{hotfixes}</span>
            <span className="text-xs text-muted-foreground">Hotfixes</span>
          </div>
        </div>
        
        <div>
          <h4 className="text-sm font-semibold mb-2 flex items-center">
            <Trophy className="h-4 w-4 mr-2 text-primary" />
            Top Bug Reporters
          </h4>
          
          {userCounts.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead className="text-right">Reports</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {userCounts.map((user) => (
                  <TableRow key={user.userId}>
                    <TableCell className="font-medium">
                      <div className="flex items-center">
                        <User className="h-4 w-4 mr-2 text-muted-foreground" />
                        <span className="truncate max-w-[200px]">
                          {user.name || user.email.split('@')[0] || 'Unknown User'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline">{user.count}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-4 text-muted-foreground">
              No bug reports submitted yet
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
